import React, { useState, useEffect } from "react";

import Breadcrumbs, { BreadcrumbLink } from "pages/Breadcrumbs";
import { CindraDashboardPopover } from "cindra/CindraDashboardPopover.tsx";
import { useCindraEnabled } from "cindra/CindraUtils";

export type ModularHeaderTab = {
    click?: () => void;
    current: boolean;
    text: string;
};

export type ModularHeaderButton = {
    img: string;
    title?: string;
    click?: () => void;
    disabled?: boolean;
    useDefaultClasses?: boolean;
    customButtonBaseClasses?: string;
    customEnabledButtonClasses?: string;
    customDisabledButtonClasses?: string;
    customEnabledTextClasses?: string;
    customDisabledTextClasses?: string;
    customEnabledImageClasses?: string;
    customDisabledImageClasses?: string;
};

export interface ModularHeaderConfig {
    trail?: BreadcrumbLink[];
    body?: {
        name?: string;
        subheader?: string;
        buttons?: ModularHeaderButton[];
        vetKitVersion?: string;
        info?: {
            provider?: any;
            physician?: any;
            coach?: any;
            facilitator?: any;
            insurers?: any;
            insurerAdmins?: any;
            tpas?: any;
            tpaAdmins?: any;
            utilizationReviewers?: any;
            utilizationReviewerAdmins?: any;
            nurseCaseManagers?: any;
            nurseCaseManagerAdmins?: any;
            employers?: any;
            employerAdmins?: any;
        };
        sideButton?: ModularHeaderButton;
        tabs?: ModularHeaderTab[];
    };
    cindra?: {
        enabled: boolean;
    };
    loading?: boolean;
}

const ModularHeader = (
    {
        children = null,
        headerConfig,
        internal
    }: {
        children?: React.ReactNode;
        headerConfig?: ModularHeaderConfig;
        internal?: boolean;
    }
) => {
    const [body, setBody] = useState(null);
    const [buttons, setButtons] = useState(null);
    const [coach, setCoach] = useState(null);
    const [facilitator, setFacilitator] = useState(null);
    const [employers, setEmployers] = useState(null);
    const [employerAdmins, setEmployerAdmins] = useState(null);
    const [insurers, setInsurers] = useState(null);
    const [insurerAdmins, setInsurerAdmins] = useState(null);
    const [name, setName] = useState(null);
    const [nurseCaseManagers, setNurseCaseManagers] = useState(null);
    const [nurseCaseManagerAdmins, setNurseCaseManagerAdmins] = useState(null);
    const [physician, setPhysician] = useState(null);
    const [provider, setProvider] = useState(null);
    const [sideButton, setSideButton] = useState(null);
    const [subheader, setSubheader] = useState(null);
    const [tabs, setTabs] = useState<ModularHeaderTab[]>(null);
    const [tpas, setTpas] = useState(null);
    const [tpaAdmins, setTpaAdmins] = useState(null);
    const [utilizationReviewers, setUtilizationReviewers] = useState(null);
    const [utilizationReviewerAdmins, setUtilizationReviewerAdmins] = useState(null);
    const [trail, setTrail] = useState<BreadcrumbLink[]>(null);
    const [vetKitVersion, setVetKitVersion] = useState(null);

    const isCindraEnabled = useCindraEnabled();

    // ---=== useEffects ===--- //

    useEffect(() => {
        if (headerConfig) {
            headerConfig.trail && setTrail(headerConfig.trail);

            if (headerConfig.body) {
                setBody(headerConfig.body);

                let body = headerConfig.body;
                body.name && setName(body.name);
                body.subheader && setSubheader(body.subheader);
                body.buttons && setButtons(body.buttons);
                body.vetKitVersion && setVetKitVersion(body.vetKitVersion);

                if (body.info) {
                    setProvider(body.info.provider);
                    setPhysician(body.info.physician);
                    setCoach(body.info.coach);
                    setFacilitator(body.info.facilitator);
                    setInsurers(body.info.insurers);
                    setInsurerAdmins(body.info.insurerAdmins);
                    setTpas(body.info.tpas);
                    setTpaAdmins(body.info.tpaAdmins);
                    setUtilizationReviewers(body.info.utilizationReviewers);
                    setUtilizationReviewerAdmins(body.info.utilizationReviewerAdmins);
                    setNurseCaseManagers(body.info.nurseCaseManagers);
                    setNurseCaseManagerAdmins(body.info.nurseCaseManagerAdmins);
                    setEmployers(body.info.employers);
                    setEmployerAdmins(body.info.employerAdmins);
                }

                body.sideButton && setSideButton(body.sideButton);
                body.tabs && setTabs(body.tabs);
            }
        }
    }, [headerConfig]);

    // Check if Cindra should be enabled
    const isCindraButtonEnabled = isCindraEnabled && headerConfig?.cindra?.enabled === true;

    // Check if we should show loading state
    const isLoading = headerConfig?.loading === true;

    // ---=== Render ===--- //

    const renderHeaderButtons = (buttons: ModularHeaderButton[]) => {
        if (!buttons) {
            return null;
        }

        return buttons?.map(
            (b: ModularHeaderButton, i: number) => {
                // Default class definitions
                const defaultButtonBaseClasses = "h-[80%] max-[1200px]:h-[70%] max-[1200px]:bottom-[15%] max-[1200px]:px-1 px-3 py-2 rounded-lg shadow border justify-center items-center gap-1 inline-flex";
                const defaultEnabledButtonClasses = "bg-white border-gray-300 hover:bg-gray-100";
                const defaultDisabledButtonClasses = "bg-gray-100 border-gray-200";
                const defaultEnabledTextClasses = "text-gray-700";
                const defaultDisabledTextClasses = "text-gray-400";
                const defaultEnabledImageClasses = "";
                const defaultDisabledImageClasses = "opacity-50";

                // Default to using default classes (true) if not specified
                const useDefaults = b.useDefaultClasses !== false;

                // Determine which classes to use for each category
                const buttonBaseClasses = b.customButtonBaseClasses || (useDefaults ? defaultButtonBaseClasses : "");
                const enabledButtonClasses = b.customEnabledButtonClasses || (useDefaults ? defaultEnabledButtonClasses : "");
                const disabledButtonClasses = b.customDisabledButtonClasses || (useDefaults ? defaultDisabledButtonClasses : "");
                const enabledTextClasses = b.customEnabledTextClasses || (useDefaults ? defaultEnabledTextClasses : "");
                const disabledTextClasses = b.customDisabledTextClasses || (useDefaults ? defaultDisabledTextClasses : "");
                const enabledImageClasses = b.customEnabledImageClasses || (useDefaults ? defaultEnabledImageClasses : "");
                const disabledImageClasses = b.customDisabledImageClasses || (useDefaults ? defaultDisabledImageClasses : "");

                // Build the final class strings
                const buttonClasses = `${buttonBaseClasses} ${b.disabled ? disabledButtonClasses : enabledButtonClasses}`.trim();
                const textClasses = `text-sm max-[1200px]:text-xs font-semibold leading-tight ${b.disabled ? disabledTextClasses : enabledTextClasses}`.trim();
                const imageClasses = `w-5 h-5 relative ${b.disabled ? disabledImageClasses : enabledImageClasses}`.trim();

                return (
                    <React.Fragment key={`header-button-${i}`}>
                        <button
                            className={`${buttonClasses} cursor-pointer disabled:cursor-not-allowed`}
                            onClick={b.disabled ? (e) => e.preventDefault() : b?.click}
                            disabled={b.disabled}
                        >
                            {
                                b.img && (
                                    <img
                                        className={imageClasses}
                                        src={b.img}
                                        alt="header-button"
                                    />
                                )
                            }
                            {
                                b.title && (
                                    <div className="px-0.5 justify-center items-center flex">
                                        <div className={textClasses}>
                                            {b.title?.replace(/(^\w)|(\s+\w)/g, letter => letter.toUpperCase())}
                                        </div>
                                    </div>
                                )
                            }
                        </button>
                    </React.Fragment>
                );
            }
        );
    };

    const renderHeaderTabs = (tabs: ModularHeaderTab[]) => {
        if (tabs === null) {
            return null;
        }

        return tabs?.map(
            (tab, i: number) => {
                return (
                    <React.Fragment key={`header-tab-${i}`}>
                        <button
                            className={`h-[32px] w-1/6 bottom-0 max-[1200px]:px-1 px-3 bg-white hover:bg-gray-100 justify-center items-center inline-flex cursor-pointer ${tab.current === true ? "border-b-2 border-brand-600 text-brand-600" : "border-0 text-gray-700"}`}
                            onClick={tab.click}
                        >
                            <div className="px-0.5 justify-center items-center flex ">
                                <div className=" text-sm max-[1200px]:text-xs  font-semibold leading-tight">{tab.text.replace(/(^\w)|(\s+\w)/g, letter => letter.toUpperCase())}</div>
                            </div>
                        </button>
                    </React.Fragment>
                )
            }
        );
    };

    const renderCindraButton = () => {
        if (!isCindraButtonEnabled) {
            return null;
        }

        return <CindraDashboardPopover />;
    };

    const renderBreadcrumbsRow = () => {
        // Render breadcrumbs row if we have trail OR if Cindra is enabled (to provide a place for the Cindra button)
        if (!trail && !isCindraButtonEnabled) {
            return null;
        }

        return (
            <span id="modular-header-breadcrumbs" className={`inline-flex items-center w-full min-h-[38px] h-[4vh] max-[1200px]:h-[4vh] pl-[12px] pr-0 py-[3px] my-0 text-sm max-[1200px]:text-xs font-normal bg-inherit shadow-none border-0`}>
                {/* Breadcrumbs container - can shrink and be overlapped */}
                <div className="flex items-center min-w-0 flex-1">
                    {trail && <Breadcrumbs links={trail}/>}
                </div>
                {/* Cindra Button positioned fixed to the viewport right edge and vertically centered in the breadcrumbs row */}
                {
                    isCindraButtonEnabled && (
                        <div className="fixed right-[12px] top-[calc(max(38px,4vh)/2-16px)] z-10">
                            {renderCindraButton()}
                        </div>
                    )
                }
            </span>
        );
    };

    return (
        <React.Fragment>
            {
                headerConfig && internal && (
                    <div id="modular-header" className={"w-full"}>
                        {/* Always render breadcrumbs row if Cindra is enabled OR if trail exists */}
                        {renderBreadcrumbsRow()}
                        {/* First divider after breadcrumbs row */}
                        {
                            (trail || isCindraButtonEnabled) && (
                                <div className="w-full h-0.5 bg-gray-300 shadow-md"></div>
                            )
                        }
                        {
                            isLoading
                                ? (
                                    // Loading state - render placeholder with same height
                                    // <span className={`inline-flex relative w-full h-auto my-0 py-2 pl-4 inset-x-0 bg-inherit shadow-none`}>
                                    //     <div className="flex-1 animate-pulse">
                                    //         <div className="flex items-center gap-x-2">
                                    //             <div className="h-8 bg-gray-200 rounded w-48"></div>
                                    //             <div className="h-6 bg-gray-200 rounded w-32"></div>
                                    //         </div>
                                    //     </div>
                                    //     <div className="flex flex-col items-end justify-center text-right mr-4">
                                    //         <div className="h-8 bg-gray-200 rounded w-16 mb-1"></div>
                                    //     </div>
                                    // </span>
                                    <span/>
                                )
                                : (
                                    body && (

                                        <span id="modular-header-body" className={`inline-flex relative w-full h-auto my-0 py-1.5 pl-4 inset-x-0 bg-inherit shadow-none`}>
                                            <div id="modular-header-patient-info" className="flex-col inline-flex justify-start items-start relative self-stretch flex-1 m-0 shadow-none">
                                                {/* First row: Patient name, subheader text, and info icon */}
                                                <div className="flex items-center w-full">
                                                    <div className="flex items-center gap-x-3">
                                                        <div className="text-4xl max-[1200px]:text-xl font-medium leading-[44px] max-[1200px]:leading-tight text-gray-900 shadow-none">
                                                            {name}
                                                        </div>
                                                        {
                                                            sideButton && (
                                                                <div className="inline-flex justify-start items-center w-9 max-[1200px]:w-8 h-full max-[1200px]:ml-1 rounded-lg cursor-pointer relative">
                                                                    <div className="has-tooltip">
                                                                        <div
                                                                            className="flex gap-2 justify-center items-center p-1 bg-white hover:bg-gray-100 border border-gray-300 rounded-lg shadow"
                                                                            onClick={() => {sideButton.click();}}
                                                                        >
                                                                            <img className="w-5 h-5 max-[1200px]:w-4 max-[1200px]:h-4 relative" src={sideButton.img} alt="side-button"/>
                                                                        </div>
                                                                        <span className="tooltip rounded shadow-lg p-1 bg-gray-900 text-white absolute left-full ml-2 top-1/2 transform -translate-y-1/2 whitespace-nowrap z-10">{sideButton.title}</span>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                        {
                                                            subheader && (
                                                                <span className="text-xl max-[1200px]:text-xs font-normal text-gray-500 shadow-none">
                                                                    {subheader}
                                                                </span>
                                                            )
                                                        }
                                                        {/* Information icon with tooltip */}
                                                        {
                                                            (provider || physician || coach || facilitator || insurers || insurerAdmins || tpas || tpaAdmins || utilizationReviewers || utilizationReviewerAdmins || nurseCaseManagers || nurseCaseManagerAdmins || employers || employerAdmins) && (
                                                                <div className="inline-flex justify-center items-center relative">
                                                                    <svg
                                                                        className="w-5 h-5 text-gray-400 hover:text-gray-600 cursor-pointer peer"
                                                                        fill="currentColor"
                                                                        viewBox="0 0 20 20"
                                                                    >
                                                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                                                                    </svg>
                                                                    <div className="absolute left-full ml-2 top-0 opacity-0 peer-hover:opacity-100 hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none peer-hover:pointer-events-auto hover:pointer-events-auto">
                                                                        <div className="bg-gray-900 text-white rounded shadow-lg p-2 whitespace-nowrap">
                                                                            <div className="grid gap-y-1 text-xs" style={{ gridTemplateColumns: "max-content 1fr" }}>
                                                                                {
                                                                                    [
                                                                                        // Always show these labeled fields with value or "None"
                                                                                        { label: "Provider", value: provider || "None", alwaysShow: true },
                                                                                        { label: "Physician", value: physician || "None", alwaysShow: true },
                                                                                        { label: "Coach", value: coach || "None", alwaysShow: true },
                                                                                        { label: "Facilitator", value: facilitator || "None", alwaysShow: true },
                                                                                        // Only show these if they have non-null values that are not "None"
                                                                                        { label: "Insurers", value: insurers, alwaysShow: false },
                                                                                        { label: "Insurer Admins", value: insurerAdmins, alwaysShow: false },
                                                                                        { label: "TPAs", value: tpas, alwaysShow: false },
                                                                                        { label: "TPA Admins", value: tpaAdmins, alwaysShow: false },
                                                                                        { label: "Utilization Reviewers", value: utilizationReviewers, alwaysShow: false },
                                                                                        { label: "Utilization Reviewer Admins", value: utilizationReviewerAdmins, alwaysShow: false },
                                                                                        { label: "Nurse Case Managers", value: nurseCaseManagers, alwaysShow: false },
                                                                                        { label: "Nurse Case Manager Admins", value: nurseCaseManagerAdmins, alwaysShow: false },
                                                                                        { label: "Employers", value: employers, alwaysShow: false },
                                                                                        { label: "Employer Admins", value: employerAdmins, alwaysShow: false }
                                                                                    ].map(({ label, value, alwaysShow }) =>
                                                                                        (alwaysShow || (value && value !== "None")) && (
                                                                                            <React.Fragment key={label}>
                                                                                                <div className="text-right pr-2">{label}:</div>
                                                                                                <div className="text-left">{value}</div>
                                                                                            </React.Fragment>
                                                                                        )
                                                                                    )
                                                                                }
                                                                            </div>
                                                                            <div className="absolute right-full top-2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-transparent border-r-gray-900"></div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                                {/* Second row: buttons beneath patient name and subheader */}
                                                <div className="flex items-center gap-2 mt-1">
                                                    {
                                                        renderHeaderButtons(buttons)
                                                    }
                                                </div>
                                            </div>
                                        </span>
                                    )
                                )
                        }
                        {
                            !isLoading && tabs && (
                                <span id="modular-header-tabs" className={`inline-flex items-end w-full h-auto min-h-[40px] pl-[1.3rem] py-0.5 my-0 text-sm max-[1200px]:text-xs font-normal bg-inherit shadow-none border-0`}>
                                    {renderHeaderTabs(tabs)}
                                </span>
                            )
                        }
                        {/* Second divider after all modular header content (only if there's body content and not loading) */}
                        {
                            (body || isLoading) && (
                                <div className="w-full h-px bg-gray-200 shadow-sm"></div>
                            )
                        }
                    </div>
                )
            }
            {children}
        </React.Fragment>
    );
};

export default ModularHeader;